import type { DashboardDTO } from '@copilot/shared';
import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import { Avatar, Card, EmptyState } from '../../components';
import { formatDate } from '../../lib/format';
import { SentimentBadge } from '../meetings/badges';
import { SentimentMarks } from '../meetings/SentimentMarks';

const SEGMENTS = [
  { key: 'positive', label: 'positive', className: 'bg-good' },
  { key: 'neutral', label: 'neutral', className: 'bg-line-strong' },
  { key: 'negative', label: 'negative', className: 'bg-danger' },
] as const;

/** Clients by sentiment, then the ones that need attention. */
export function ClientHealthCard({
  health,
  clients,
  style,
}: {
  health: DashboardDTO['clientHealth'] | undefined;
  clients: DashboardDTO['clientsNeedingAttention'];
  style?: CSSProperties;
}) {
  const known = health ? health.positive + health.neutral + health.negative : 0;
  const met = health ? known + health.noData : 0;
  return (
    <Card
      style={style}
      title="Client health"
      description={
        health ? `${met} ${met === 1 ? 'client' : 'clients'} met in this period` : undefined
      }
    >
      {!health || known === 0 ? (
        <EmptyState title="No client sentiment in this period yet." />
      ) : (
        <>
          <div className="flex h-3 gap-0.5 overflow-hidden rounded-full" aria-hidden>
            {SEGMENTS.filter((s) => health[s.key] > 0).map((s) => (
              <span
                key={s.key}
                className={`grow-x ${s.className}`}
                style={{ flex: health[s.key] }}
              />
            ))}
          </div>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
            {SEGMENTS.map((s) => (
              <li key={s.key} className="flex items-center gap-1.5">
                <span aria-hidden className={`h-2 w-2 rounded-full ${s.className}`} />
                {health[s.key]} {s.label}
              </li>
            ))}
            {health.noData > 0 && <li>{health.noData} without sentiment yet</li>}
          </ul>

          <h3 className="mt-5 mb-1 text-xs font-semibold tracking-wide text-muted uppercase">
            Needs attention
          </h3>
          {clients.length === 0 ? (
            <p className="py-2 text-sm text-muted">
              No clients with negative sentiment in this period.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {clients.map((c, i) => (
                <li
                  key={c.clientId}
                  className="rise flex flex-wrap items-center justify-between gap-2 py-2.5"
                  style={{ '--i': i } as CSSProperties}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar name={c.clientName} size="md" />
                    <div className="min-w-0">
                      <Link
                        to={`/clients/${c.clientId}`}
                        className="text-sm font-medium text-ink hover:text-accent-ink hover:underline"
                      >
                        {c.clientName}
                      </Link>
                      <div className="truncate text-xs text-muted">
                        last met {formatDate(c.lastMeetingAt)}
                        {c.ownerName ? ` · ${c.ownerName}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <SentimentMarks levels={c.recent ?? []} />
                    <SentimentBadge level={c.level} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
